import { ARENA, DEFAULT_STATS, RESOURCE_TABLE, SHIELD_HEAL } from "@cc/engine";
import type { ActionType, ResourceKind } from "@cc/protocol";
import type { Dict, Locale } from "./i18n";
import { actionName, resourceKindName } from "./i18n";
import { navigate } from "./router";
import { RESOURCE_STYLES } from "./theme";

/**
 * The rulebook, condensed to a narrow strip on the right of the arena.
 *
 * Same design rule as the full page: every number comes from the engine
 * constants, so the rules cannot describe a game the simulation does not play.
 * Kept narrow on purpose — glanceable reference beside the fight, not a page.
 */
export function RulesStrip({ locale, d }: { locale: Locale; d: Dict }) {
  return (
    <aside className="rules-strip">
      <div className="strip-head">
        <span>{d.navRules}</span>
        <button className="strip-link" onClick={() => navigate({ name: "rules" })}>
          {d.rulesStripHint} →
        </button>
      </div>

      <section className="strip-block">
        <h4>{d.rulesObjective}</h4>
        <p className="strip-note">{d.stripObjective}</p>
      </section>

      <section className="strip-block">
        <ArenaMini />
        <p className="strip-note">{d.rulesPhaseNote}</p>
      </section>

      <section className="strip-block">
        <h4>{d.rulesVision}</h4>
        <p className="strip-note">{d.stripVision}</p>
      </section>

      <section className="strip-block">
        <h4>{d.rulesItems}</h4>
        <ul className="strip-list">
          {RESOURCE_TABLE.map((template) => {
            const style = RESOURCE_STYLES[template.kind];
            return (
              <li key={template.kind}>
                <span className="glyph" style={{ color: style.color }}>
                  {style.glyph}
                </span>
                <span className="name">{resourceKindName(template.kind, locale)}</span>
                <span className="val">{briefFor(template.kind, template.value, d)}</span>
              </li>
            );
          })}
        </ul>
      </section>

      <section className="strip-block">
        <h4>{d.rulesActions}</h4>
        <ul className="strip-list strip-actions">
          {(
            [
              { a: "MOVE", v: `${DEFAULT_STATS.speed}/tick` },
              { a: "SCOUT", v: `r${ARENA.scoutRadius} · ${ARENA.scoutTicks}t` },
              { a: "COLLECT", v: `r${DEFAULT_STATS.collectRange} · ${DEFAULT_STATS.collectTicks}t` },
              { a: "ATTACK", v: `${DEFAULT_STATS.attackDamage} · r${DEFAULT_STATS.attackRange}` },
              { a: "DEFEND", v: `−${Math.round(ARENA.defendDamageReduction * 100)}%` },
              { a: "FLEE", v: `${ARENA.fleeSpeedMul}× · ${ARENA.fleeDurationTicks}t` },
              { a: "EXCHANGE", v: `${ARENA.exchangeCost.bomb}/${ARENA.exchangeCost.blade}` },
              {
                a: "USE",
                v: `${resourceKindName("bomb", locale)} / ${resourceKindName("blade", locale)}`,
              },
              { a: "WAIT", v: d.stripNothing },
            ] as { a: ActionType; v: string }[]
          ).map((row) => (
            <li key={row.a}>
              <span className="name mono">{actionName(row.a, locale)}</span>
              <span className="val mono">{row.v}</span>
            </li>
          ))}
        </ul>
      </section>

      <section className="strip-block">
        <h4>{d.rulesScoring}</h4>
        <p className="strip-note">{d.stripScoring}</p>
      </section>

      <section className="strip-block">
        <h4>{d.stripTipsTitle}</h4>
        <ul className="strip-tips">
          <li>{d.stripTip1}</li>
          <li>{d.stripTip2}</li>
          <li>{d.stripTip3}</li>
        </ul>
      </section>
    </aside>
  );
}

/**
 * The trap's table value is its *bait* value, not a reward, so showing "+30"
 * beside it would invert the truth. It is labelled by what it does instead.
 */
function briefFor(kind: ResourceKind, value: number, d: Dict): string {
  if (kind === "trap") return d.stripLandmine(ARENA.trapDamage);
  if (kind === "shield") return `+${value} · ${d.stripHeals(SHIELD_HEAL)}`;
  if (kind === "bomb") return d.itemBombBrief(ARENA.bombDamage, ARENA.bombRadius);
  if (kind === "blade") return d.itemBladeBrief(ARENA.bladeDamagePerTick, ARENA.bladeRange);
  return `+${value}`;
}

/**
 * The arena diagram, shrunk for the strip.
 *
 * Identical geometry to the full page's diagram. Two renderings of the same
 * claim would be two chances to drift apart, so the numbers are shared and only
 * the presentation differs.
 */
function ArenaMini() {
  const arena = 100;
  return (
    <svg className="strip-diagram" viewBox={`0 0 ${arena} ${arena}`} role="img" aria-hidden="true">
      <rect x={0} y={0} width={arena} height={arena} fill="var(--panel-2)" stroke="var(--line)" />
      <circle
        cx={6}
        cy={6}
        r={DEFAULT_STATS.visionRadius}
        fill="#ff2d55"
        fillOpacity={0.1}
        stroke="#ff2d55"
        strokeOpacity={0.45}
      />
      <circle
        cx={94}
        cy={94}
        r={DEFAULT_STATS.visionRadius}
        fill="#8b5cf6"
        fillOpacity={0.1}
        stroke="#8b5cf6"
        strokeOpacity={0.45}
      />
      <circle cx={6} cy={6} r={2} fill="#ff2d55" />
      <circle cx={94} cy={94} r={2} fill="#8b5cf6" />
      <circle cx={42} cy={30} r={1.8} fill={RESOURCE_STYLES.crystal.color} />
      <circle cx={62} cy={70} r={1.8} fill={RESOURCE_STYLES.energy.color} />
      <circle cx={30} cy={78} r={1.8} fill={RESOURCE_STYLES.shield.color} />
      <circle cx={72} cy={40} r={1.8} fill={RESOURCE_STYLES.crystal.color} />
    </svg>
  );
}
